/**
 * Geolocation - Singleton for location detection and GDPR compliance
 *
 * SINGLETON: Detects user location via XHR headers from server.
 * Used for GDPR compliance, region-specific features, and localization.
 *
 * PUBLIC API NOTE: Public methods auto-inject caller context as first argument.
 * JSDoc @param lists show only user-provided parameters (what you actually pass).
 *
 * FEATURES:
 * - Continent detection (EU, NA, etc.)
 * - Country detection (US, UK, CA, etc.)
 * - GDPR region detection
 * - Cached location data
 *
 * @class Geolocation
 * @extends Class
 * @singleton
 *
 * @example
 * // Get user location
 * Geolocation.get().then(function(geo) {
 *     const { in_us, in_eu, in_gdpr } = geo;
 *     // Adjust behavior based on location
 * });
 */
define("Geolocation", [`Class`, `module`], function (Super, Self) {
	const GDPR = [`UK`, `GB`, `CA`, `JP`];

	return Super.inherit({
		namespace: Self.id,
		singleton: true, // utility class, only one instance
		requires: [`XHR`],
		api: {
			/**
			 * Get user geolocation data.
			 *
			 * @method Geolocation#get
			 * @param {Object} [options] - Request options
			 * @returns {Promise<Object>} Geolocation object with in_us, in_eu, in_gdpr, in_na flags
			 */
			get: {
				public: true,
				async: true,
				async value(context, options) {
					try {
						const { Config, XHR } = this;
						const { COUNTRY_PREFIX_OPTIONS } = Config;
						const promises = [XHR.getContinentCode(), XHR.getCountryCode()];
						const [continent, country] = await Promise.all(promises);
						const in_eu = continent === `EU`;
						const in_gdpr = in_eu || GDPR.includes(country);
						const in_na = continent === `NA`;
						const in_us = country === `US`;
						let countryCode = null;

						this.each(COUNTRY_PREFIX_OPTIONS, (option) => {
							if (option.code === country) {
								countryCode = option.value;
								return false;
							}
						});

						return { in_eu, in_gdpr, in_na, in_us, country_code: countryCode };
					} catch (error) {
						this.log({
							priority: 3,
							type: `warning`,
							message: `Geolocation API error: %O`,
							args: [error],
						});

						// fallback, local development
						return {
							in_eu: false,
							in_gdpr: false,
							in_na: true,
							in_us: true,
							country_code: null,
						};
					}
				},
			},
		},
	});
});

//# sourceMappingURL=Geolocation.js.map